import { Col, Form, Input, Modal, Radio, Row, Select, Spin, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import mealApi from '../../api/meal.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/sales.module.scss'
import { mealCategory } from './modeal'

// 반려 사유 enum과 한국어 매핑
const rejectionReasons = [
  { value: 'ALREADY_REGISTERED', label: '이미 등록된 음식' },
  { value: 'NON_FOOD_ITEM', label: '비식품 항목' },
  { value: 'UNCLEAR_INFORMATION', label: '정보 불명확' },
  { value: 'OFFENSIVE_LANGUAGE', label: '욕설/비속어' },
  { value: 'OTHER', label: '기타' },
]

// 상태 옵션
const statusOptions = [
  { value: null, label: '전체' },
  { value: 'UNDER_REVIEW', label: '검토중' },
  { value: 'APPROVED', label: '승인' },
  { value: 'REJECTED', label: '반려' },
]

const MealRequestListPage = () => {
  const [mealRequests, setMealRequests] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const navigate = useNavigate()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectForm, setRejectForm] = useState({
    reason: null,
    rejectAdminReason: '',
  })
  const [statusFilter, setStatusFilter] = useState(null) // 검색용

  const getMealRequests = async (page = 0, size = 10, status = null) => {
    setIsLoading(true)
    const params = {
      page,
      size,
      ...(status && { status }), // status가 있을 때만 파라미터에 추가
    }
    try {
      const res = await mealApi.getMealRequestList(params)

      if (res.status === 200) {
        setMealRequests({
          content: res.data.content,
          pageable: {
            pageNumber: res.data.pageable?.pageNumber || page,
            pageSize: res.data.pageable?.pageSize || size,
          },
          totalElements: res.data.totalElements || 0,
        })
      }
    } catch (err) {
      console.error('getMealRequests error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getMealRequests(0, 10, statusFilter)
  }, [statusFilter]) // statusFilter 변경 시 자동으로 데이터 재조회

  // 승인 처리 함수
  const handleApproval = async (record) => {
    setIsLoading(true)
    try {
      const param = {
        mealCategoryId: record.mealCategory,
        mealName: record.mealName,
      }
      await mealApi.approveMealRequest(record.id, param)
      await getMealRequests(0, mealRequests.pageable?.pageSize, statusFilter)
      setSelectedRowKeys([])
    } catch (error) {
      console.error('승인 처리 중 오류:', error)
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  // 반려 처리 함수
  const handleRejection = async (record) => {
    setIsLoading(true)
    try {
      const param = {
        reason: rejectForm.reason,
        rejectAdminReason:
          rejectForm.reason === 'ALREADY_REGISTERED' ||
          rejectForm.reason === 'OTHER'
            ? rejectForm.rejectAdminReason
            : null,
      }
      await mealApi.rejectMealRequest(record.id, param)
      await getMealRequests(0, mealRequests.pageable?.pageSize, statusFilter)
      setSelectedRowKeys([])
      setShowRejectModal(false)
      setRejectForm({ reason: null, rejectAdminReason: '' })
    } catch (error) {
      console.error('거절 처리 중 오류:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const columns = [
    {
      title: '요청 번호',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '회원 ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: '음식명',
      dataIndex: 'mealName',
      key: 'mealName',
      render: (text, record) => {
        return (
          <input
            type="text"
            value={text || ''}
            onChange={(e) => {
              // 실시간으로 데이터 업데이트
              const newData = [...mealRequests.content]
              const index = newData.findIndex((item) => item.id === record.id)
              if (index > -1) {
                newData[index] = { ...newData[index], mealName: e.target.value }
                setMealRequests((prev) => ({
                  ...prev,
                  content: newData,
                }))
              }
            }}
            disabled={record.status !== 'UNDER_REVIEW'}
            style={{
              width: '100%',
              padding: '8px 12px',
              border:
                record.status === 'UNDER_REVIEW'
                  ? '1px solid #d9d9d9'
                  : '1px solid #f0f0f0',
              borderRadius: 6,
              outline: 'none',
              backgroundColor:
                record.status === 'UNDER_REVIEW' ? '#ffffff' : '#f9f9f9',
              color: record.status === 'UNDER_REVIEW' ? '#000000' : '#999999',
              cursor: record.status === 'UNDER_REVIEW' ? 'text' : 'not-allowed',
              fontSize: '14px',
              transition: 'all 0.2s ease',
            }}
            onFocus={(e) => {
              if (record.status === 'UNDER_REVIEW') {
                e.target.style.border = '2px solid #1890ff'
                e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)'
              }
            }}
            onBlur={(e) => {
              e.target.style.border =
                record.status === 'UNDER_REVIEW'
                  ? '1px solid #d9d9d9'
                  : '1px solid #f0f0f0'
              e.target.style.boxShadow = 'none'
              // 저장 처리
              console.log('저장:', record.id, e.target.value)
            }}
            placeholder={
              record.status === 'UNDER_REVIEW' ? '음식명을 입력하세요' : ''
            }
          />
        )
      },
    },
    {
      title: '음식 카테고리 선택',
      dataIndex: 'mealCategory',
      key: 'mealCategory',
      render: (text, record) => {
        const categoryOptions = mealCategory.map((category) => ({
          value: category.id,
          label: category.categoryName,
        }))

        return (
          <Select
            style={{ width: '100%' }}
            options={categoryOptions}
            value={record.mealCategory || undefined}
            placeholder="카테고리를 선택하세요"
            disabled={record.status !== 'UNDER_REVIEW'}
            onChange={(value) => {
              // 실시간으로 데이터 업데이트
              const newData = [...mealRequests.content]
              const index = newData.findIndex((item) => item.id === record.id)
              if (index > -1) {
                newData[index] = { ...newData[index], mealCategory: value }
                setMealRequests((prev) => ({
                  ...prev,
                  content: newData,
                }))
              }
              console.log('카테고리 변경:', record.id, value)
            }}
          />
        )
      },
    },
    {
      title: '요청 일시',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (value) => {
        if (value === 'REJECTED') return '반려'
        if (value === 'APPROVED') return '승인'
        if (value === 'UNDER_REVIEW') return '검토중'
        return value
      },
    },
    {
      title: '반려 사유',
      key: 'rejectionInfo',
      width: 200,
      render: (_, record) => {
        if (record.status !== 'REJECTED' || !record.rejectionReason) return '-'

        const reasonText =
          rejectionReasons.find(
            (reason) => reason.value === record.rejectionReason,
          )?.label || record.rejectionReason

        return (
          <div style={{ fontSize: '12px' }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '4px',
                color: '#EB516F',
              }}
            >
              {reasonText}
            </div>
            {record.rejectAdminReason && (
              <div
                style={{ color: '#666', fontSize: '11px', lineHeight: '1.4' }}
              >
                {record.rejectAdminReason}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: '처리',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => {
        if (record.status !== 'UNDER_REVIEW') return null

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 16,
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                background:
                  !record.mealCategory || !record.mealName?.trim()
                    ? '#f5f5f5'
                    : '#EDF4FF',
                color:
                  !record.mealCategory || !record.mealName?.trim()
                    ? '#999999'
                    : '#117FFA',
                borderRadius: 4,
                padding: '4px 12px',
                cursor: !record.mealCategory ? 'not-allowed' : 'pointer',
                border: !record.mealCategory
                  ? '1px solid #d9d9d9'
                  : '1px solid #117FFA',
              }}
              disabled={!record.mealCategory || !record.mealName?.trim()}
              onClick={(e) => {
                e.stopPropagation()
                if (record.mealCategory) {
                  setSelectedRecord(record)
                  setShowApproveModal(true)
                }
              }}
            >
              승인
            </button>
            <button
              style={{
                background: !record.mealName?.trim() ? '#f5f5f5' : '#FFF0F2',
                color: !record.mealName?.trim() ? '#999999' : '#EB516F',
                border: !record.mealName?.trim()
                  ? '#d9d9d9'
                  : '1px solid #EB516F',
                borderRadius: 4,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
              onClick={async (e) => {
                e.stopPropagation()
                setSelectedRecord(record)
                setShowRejectModal(true)
              }}
              disabled={!record.mealName?.trim()}
            >
              거절
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Spin spinning={isLoading}>
        <ContentHeader title="음식 요청 관리" />
        <div
          className={styles['product-register-wrap']}
          style={{ minWidth: '1024px' }}
        >
          {/* 전체 카운트 */}
          <Row
            justify="space-around"
            className={styles['product-search-product-status-wrap']}
            style={{ marginTop: 20 }}
          >
            <Col className={styles['product-status-box-col-wrap']} span={6}>
              <Row className={styles['product-status-box-wrap']}>
                <div className={styles['product-status-text']}>
                  <div>전체</div>
                  <div>
                    <span className={styles['product-status-num']}>
                      {mealRequests.totalElements}
                    </span>{' '}
                    건
                  </div>
                </div>
              </Row>
            </Col>
          </Row>

          {/* 상태 필터 */}
          <div className={styles['product-search-box-wrap']}>
            <Form.Item
              className={styles['center-label']}
              label="상태"
              labelAlign="left"
              colon={false}
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 21 }}
            >
              <Select
                style={{ width: 200, textAlign: 'left' }}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
                options={statusOptions}
                placeholder="상태를 선택하세요"
              />
            </Form.Item>
          </div>

          {/* 목록 헤더 */}
          <Row
            className={styles['product-register-border-box']}
            wrap={false}
            style={{ marginTop: 20, borderBottom: 0 }}
          >
            <span className={styles['product-register-box-title']}>
              음식 요청 목록 (총{' '}
              <span
                className={styles['product-register-box-subtitle']}
                style={{ marginLeft: 0 }}
              >
                {mealRequests.totalElements}
              </span>
              개)
            </span>
          </Row>

          {/* 목록 테이블 */}
          <div
            className={styles['product-register-border-box']}
            style={{ padding: '20px 25px 40px', height: 'fit-content' }}
          >
            <Table
              columns={columns}
              dataSource={mealRequests.content}
              loading={isLoading}
              bordered
              size="middle"
              rowKey="id"
              pagination={{
                current: (mealRequests.pageable?.pageNumber || 0) + 1,
                pageSize: mealRequests.pageable?.pageSize || 10,
                total: mealRequests.totalElements,
                onChange: (page, pageSize) => {
                  getMealRequests(page - 1, pageSize, statusFilter)
                },
                showSizeChanger: false,
              }}
            />
          </div>
        </div>

        {/* 승인 확인 모달 */}
        <Modal
          title="승인 확인"
          open={showApproveModal}
          onCancel={() => setShowApproveModal(false)}
          onOk={() => {
            handleApproval(selectedRecord)
            setShowApproveModal(false)
          }}
          okText="승인"
          cancelText="취소"
          width={500}
          okButtonProps={{
            style: {
              backgroundColor: '#EDF4FF',
              borderColor: '#117FFA',
              color: '#117FFA',
            },
          }}
          disabled={
            !selectedRecord?.mealCategory || !selectedRecord?.mealName?.trim()
          }
        >
          {selectedRecord && (
            <div style={{ padding: '20px 0' }}>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                다음 내용으로 승인하시겠습니까?
              </p>
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <strong>음식명:</strong>
                  <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                    {selectedRecord.mealName?.trim() || '입력되지 않음'}
                  </span>
                </div>
                <div>
                  <strong>카테고리:</strong>
                  <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                    {(() => {
                      const selectedCategory = mealCategory.find(
                        (cat) => cat.id === selectedRecord.mealCategory,
                      )
                      return selectedCategory
                        ? selectedCategory.categoryName
                        : '선택되지 않음'
                    })()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* 반려 모달 */}
        <Modal
          title="반려 확인"
          open={showRejectModal}
          onCancel={() => {
            setShowRejectModal(false)
            setRejectForm({ reason: null, rejectAdminReason: '' })
          }}
          onOk={() => {
            handleRejection(selectedRecord)
          }}
          okText="반려"
          cancelText="취소"
          width={600}
          okButtonProps={{
            style: {
              backgroundColor:
                !rejectForm.reason ||
                ((rejectForm.reason === 'ALREADY_REGISTERED' ||
                  rejectForm.reason === 'OTHER') &&
                  !rejectForm.rejectAdminReason.trim())
                  ? '#f5f5f5'
                  : '#FFF0F2',
              borderColor:
                !rejectForm.reason ||
                ((rejectForm.reason === 'ALREADY_REGISTERED' ||
                  rejectForm.reason === 'OTHER') &&
                  !rejectForm.rejectAdminReason.trim())
                  ? '#d9d9d9'
                  : '#EB516F',
              color:
                !rejectForm.reason ||
                ((rejectForm.reason === 'ALREADY_REGISTERED' ||
                  rejectForm.reason === 'OTHER') &&
                  !rejectForm.rejectAdminReason.trim())
                  ? '#999999'
                  : '#EB516F',
              cursor:
                !rejectForm.reason ||
                ((rejectForm.reason === 'ALREADY_REGISTERED' ||
                  rejectForm.reason === 'OTHER') &&
                  !rejectForm.rejectAdminReason.trim())
                  ? 'not-allowed'
                  : 'pointer',
            },
            disabled:
              !rejectForm.reason ||
              ((rejectForm.reason === 'ALREADY_REGISTERED' ||
                rejectForm.reason === 'OTHER') &&
                !rejectForm.rejectAdminReason.trim()),
          }}
        >
          {selectedRecord && (
            <div style={{ padding: '20px 0' }}>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                <strong>{selectedRecord.mealName}</strong> 요청을
                반려하시겠습니까?
              </p>

              <Form layout="vertical">
                <Form.Item
                  label="반려 사유"
                  required
                  style={{ marginBottom: '20px' }}
                >
                  <Radio.Group
                    value={rejectForm.reason}
                    onChange={(e) => {
                      setRejectForm({
                        ...rejectForm,
                        reason: e.target.value,
                        rejectAdminReason: '', // 사유 변경 시 메모 초기화
                      })
                    }}
                    style={{ width: '100%' }}
                  >
                    {rejectionReasons.map((reason) => (
                      <Radio
                        key={reason.value}
                        value={reason.value}
                        style={{
                          display: 'block',
                          height: '40px',
                          lineHeight: '40px',
                          marginLeft: 0,
                        }}
                      >
                        {reason.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>

                {(rejectForm.reason === 'ALREADY_REGISTERED' ||
                  rejectForm.reason === 'OTHER') && (
                  <Form.Item
                    label="관리자 메모"
                    required
                    style={{ marginBottom: 0 }}
                  >
                    <Input.TextArea
                      placeholder={
                        rejectForm.reason === 'ALREADY_REGISTERED'
                          ? '이미 등록된 음식명을 입력해주세요. 음식명만 입력해주세요! (예: 김치찌개)'
                          : '반려 사유가 슈가코치에 그대로 표기됩니다. (50자 이내)'
                      }
                      value={rejectForm.rejectAdminReason}
                      onChange={(e) => {
                        setRejectForm({
                          ...rejectForm,
                          rejectAdminReason: e.target.value,
                        })
                      }}
                      rows={3}
                      maxLength={50}
                      showCount
                    />
                  </Form.Item>
                )}
              </Form>
            </div>
          )}
        </Modal>
      </Spin>
    </>
  )
}

export default MealRequestListPage
